import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Check, Search, X } from 'lucide-react';

/**
 * PremiumSelect - A unified, high-aesthetic dropdown component
 * matching the Aptiview design system.
 *
 * Supports:
 * - Icons with thematic color accents
 * - Custom styled floating menu (no native Windows grey popup!)
 * - Search filter for large option lists (> 6 items)
 * - Drop-up option for bottom pagination footers
 * - Selected checkmarks and smooth hover animations
 * - Click-outside & Escape key handling
 */
const PremiumSelect = ({
    label,
    icon: Icon,
    iconColor = 'text-blue-600',
    value,
    onChange,
    options = [],
    placeholder = 'Select option...',
    searchPlaceholder = 'Search...',
    searchable = null, // auto-detected or explicit boolean
    size = 'md',       // 'sm' (compact for footers) | 'md' (standard for filters)
    dropUp = false,    // opens upwards (ideal for bottom footers)
    align = 'left',    // 'left' | 'right' alignment of floating menu
    className = '',
    menuWidth = 'w-full',
    disabled = false,
    variant = 'default', // 'default' (admin) | 'b2c' (candidate portal matching MyProgress)
}) => {
    const isB2C = variant === 'b2c';
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const containerRef = useRef(null);
    const searchInputRef = useRef(null);

    // Normalize options into { value, label, isGroupHeader, badge } format, supporting nested optgroups
    const normalizedOptions = useMemo(() => {
        const list = [];
        (options || []).forEach((opt) => {
            if (opt && typeof opt === 'object') {
                if (opt.group && Array.isArray(opt.options)) {
                    list.push({ isGroupHeader: true, label: String(opt.group) });
                    opt.options.forEach((sub) => {
                        if (typeof sub === 'object' && sub !== null) {
                            list.push({
                                value: sub.value !== undefined ? sub.value : '',
                                label: sub.label !== undefined ? String(sub.label) : String(sub.value || ''),
                                badge: sub.badge || null,
                            });
                        } else {
                            list.push({ value: sub, label: String(sub) });
                        }
                    });
                } else if (opt.isGroupHeader) {
                    list.push({ isGroupHeader: true, label: String(opt.label || '') });
                } else {
                    list.push({
                        value: opt.value !== undefined ? opt.value : '',
                        label: opt.label !== undefined ? String(opt.label) : String(opt.value || ''),
                        badge: opt.badge || null,
                    });
                }
            } else {
                list.push({
                    value: opt,
                    label: String(opt),
                });
            }
        });
        return list;
    }, [options]);

    // Find currently selected option
    const selectedOption = useMemo(() => {
        if (value === '' || value === null || value === undefined) {
            return null;
        }
        return normalizedOptions.find((opt) => !opt.isGroupHeader && String(opt.value) === String(value)) || null;
    }, [normalizedOptions, value]);

    // Selectable options count for search detection
    const selectableCount = useMemo(() => {
        return normalizedOptions.filter(o => !o.isGroupHeader).length;
    }, [normalizedOptions]);

    // Auto-enable search bar by default for dropdowns (unless explicitly disabled with searchable={false})
    const shouldShowSearch = searchable !== null ? Boolean(searchable) : selectableCount > 1;

    // Filter options by search query
    const filteredOptions = useMemo(() => {
        if (!searchQuery.trim()) return normalizedOptions;
        const q = searchQuery.toLowerCase().trim();
        return normalizedOptions.filter((opt) =>
            opt.isGroupHeader || opt.label.toLowerCase().includes(q)
        );
    }, [normalizedOptions, searchQuery]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
                setSearchQuery('');
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Keyboard support: Escape closes dropdown
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) {
                setIsOpen(false);
                setSearchQuery('');
            }
        };
        if (isOpen) {
            window.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    // Focus search input when opened
    useEffect(() => {
        if (isOpen && shouldShowSearch && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 60);
        }
    }, [isOpen, shouldShowSearch]);

    const handleSelect = (optValue) => {
        if (onChange) {
            onChange(optValue);
        }
        setIsOpen(false);
        setSearchQuery('');
    };

    const isCompact = size === 'sm';

    // Display text in button: selectedOption label, or placeholder
    const displayLabel = selectedOption ? selectedOption.label : placeholder;
    const hasSelection = Boolean(selectedOption);

    return (
        <div className={`relative ${isOpen ? 'z-50' : 'z-10'} ${className}`} ref={containerRef}>
            {/* Optional Top Label */}
            {label && (
                <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 mb-1.5 select-none">
                    {Icon && <Icon size={14} className={`${iconColor} flex-shrink-0`} />}
                    <span className="truncate">{label}</span>
                </label>
            )}

            {/* Trigger Button */}
            <button
                type="button"
                onClick={() => !disabled && setIsOpen((prev) => !prev)}
                disabled={disabled}
                className={`w-full flex items-center justify-between text-left transition-all duration-150 outline-none
                    ${isB2C
                        ? `rounded-xl border ${
                            disabled
                                ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                                : isOpen
                                    ? 'bg-white border-indigo-500 ring-2 ring-indigo-500/15 shadow-sm'
                                    : 'bg-white border-gray-200 hover:border-gray-300 text-gray-700 shadow-2xs hover:bg-gray-50/50'
                          } ${isCompact ? 'px-3 py-1.5 text-xs' : 'px-4 py-2.5 text-xs sm:text-sm font-semibold'}`
                        : `rounded-lg border ${
                            isCompact ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-sm'
                          } ${
                            disabled
                                ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
                                : isOpen
                                    ? 'bg-white border-blue-500 ring-2 ring-blue-500/15 shadow-sm'
                                    : 'bg-white border-slate-300 hover:border-slate-400 text-slate-700 shadow-xs'
                          }`
                    }`}
            >
                <div className="flex items-center gap-2 min-w-0 flex-1 mr-1">
                    {!label && Icon && (
                        <Icon size={isCompact ? 13 : 15} className={`${iconColor} flex-shrink-0`} />
                    )}
                    <span
                        className={`truncate ${
                            hasSelection
                                ? (isB2C ? 'font-bold text-gray-850' : 'font-medium text-slate-800')
                                : placeholder
                                    ? (isB2C ? 'font-medium text-gray-600' : 'font-medium text-slate-700')
                                    : 'text-slate-400 font-normal'
                        }`}
                        title={displayLabel}
                    >
                        {displayLabel}
                    </span>
                </div>

                <ChevronDown
                    size={isCompact ? 13 : 15}
                    className={`flex-shrink-0 transition-transform duration-200 ${
                        isOpen
                            ? (dropUp ? '-rotate-180 ' + (isB2C ? 'text-indigo-600' : 'text-blue-600') : 'rotate-180 ' + (isB2C ? 'text-indigo-600' : 'text-blue-600'))
                            : 'text-gray-400'
                    }`}
                />
            </button>

            {/* Floating Dropdown Menu */}
            {isOpen && (
                <div
                    className={`absolute z-50 rounded-xl shadow-2xl border overflow-hidden transition-all duration-150
                        ${isB2C ? 'border-gray-200/90 py-1' : 'border-slate-200/90 py-1.5'}
                        ${menuWidth}
                        ${align === 'right' ? 'right-0' : 'left-0'}
                        ${dropUp ? 'bottom-full mb-1.5' : 'top-full mt-1.5'}
                    `}
                    style={{ minWidth: isCompact ? '120px' : '180px', backgroundColor: '#ffffff' }}
                >
                    {/* Search bar inside menu */}
                    {shouldShowSearch && (
                        <div className="px-2 pt-1 pb-1.5 border-b border-slate-100">
                            <div className="relative flex items-center">
                                <Search size={13} className="absolute left-2.5 text-slate-400 pointer-events-none" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder={searchPlaceholder || 'Search...'}
                                    className={`w-full pl-7 pr-7 py-1 text-xs bg-slate-50 border border-slate-200 rounded-md outline-none text-slate-700 transition-colors ${
                                        isB2C ? 'focus:border-indigo-400 focus:bg-white' : 'focus:border-blue-500 focus:bg-white'
                                    }`}
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => {
                                        e.stopPropagation();
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            const firstSelectable = filteredOptions.find((o) => !o.isGroupHeader);
                                            if (firstSelectable) {
                                                handleSelect(firstSelectable.value);
                                            }
                                        }
                                    }}
                                />
                                {searchQuery && (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSearchQuery('');
                                            searchInputRef.current?.focus();
                                        }}
                                        className="absolute right-2 text-slate-400 hover:text-slate-600 p-0.5 rounded cursor-pointer"
                                        title="Clear"
                                    >
                                        <X size={12} />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Options List */}
                    <div className="max-h-56 overflow-y-auto py-0.5 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar-thumb]:rounded-full">
                        {/* Placeholder option (e.g. All Companies) if placeholder provided and not already in options */}
                        {placeholder && (!searchQuery || placeholder.toLowerCase().includes(searchQuery.toLowerCase())) && !normalizedOptions.some((o) => !o.isGroupHeader && o.value === '') && (
                            <button
                                type="button"
                                onClick={() => handleSelect('')}
                                className={`w-full text-left text-xs sm:text-sm flex items-center justify-between transition-colors
                                    ${isB2C ? 'px-4 py-2.5' : 'px-3 py-1.5'}
                                    ${value === '' || value === null || value === undefined
                                        ? (isB2C ? 'text-indigo-600 bg-indigo-50/60 font-bold' : 'bg-blue-50/80 text-blue-700 font-semibold')
                                        : 'text-slate-600 hover:bg-slate-50'
                                    }`}
                            >
                                <span className="truncate">{placeholder}</span>
                                {(value === '' || value === null || value === undefined) && (
                                    <Check size={13} className={`${isB2C ? 'text-indigo-600' : 'text-blue-600'} flex-shrink-0 ml-2`} />
                                )}
                            </button>
                        )}

                        {filteredOptions.length === 0 ? (
                            <div className="px-3 py-3 text-center text-xs text-slate-400">
                                No matching options
                            </div>
                        ) : (
                            filteredOptions.map((opt, index) => {
                                if (opt.isGroupHeader) {
                                    return (
                                        <div
                                            key={`group-${opt.label}-${index}`}
                                            className="px-3.5 pt-2 pb-1 text-[11px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50/60 border-y border-slate-100 first:border-t-0"
                                        >
                                            {opt.label}
                                        </div>
                                    );
                                }

                                const isSelected = String(opt.value) === String(value);
                                return (
                                    <button
                                        key={String(opt.value)}
                                        type="button"
                                        onClick={() => handleSelect(opt.value)}
                                        className={`w-full text-left text-xs sm:text-sm flex items-center justify-between transition-colors
                                            ${isB2C ? 'px-4 py-2.5' : 'px-3 py-1.5'}
                                            ${isSelected
                                                ? (isB2C ? 'text-indigo-600 bg-indigo-50/60 font-bold' : 'bg-blue-50/80 text-blue-700 font-semibold')
                                                : (isB2C ? 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 font-medium' : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900')
                                            }`}
                                    >
                                        <span className="truncate mr-2" title={opt.label}>
                                            {opt.label}
                                        </span>
                                        {opt.badge && (
                                            <span className="text-[10px] px-1.5 py-0.2 rounded bg-slate-100 text-slate-600 font-normal mr-1">
                                                {opt.badge}
                                            </span>
                                        )}
                                        {isSelected && (
                                            <Check size={13} className={`${isB2C ? 'text-indigo-600' : 'text-blue-600'} flex-shrink-0 ml-auto`} />
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default React.memo(PremiumSelect);
